import React from 'react';

/**
 * Safely parse rich text with **bold** and _italic_ markers
 * Returns React elements instead of using dangerouslySetInnerHTML
 */
const parseRichText = (text) => {
  const parts = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    const boldMatch = remaining.match(/\*\*(.*?)\*\*/);
    const italicMatch = remaining.match(/_(.*?)_/);

    let nextMatch = null;
    let type = null;

    if (boldMatch && italicMatch) {
      if (boldMatch.index <= italicMatch.index) {
        nextMatch = boldMatch;
        type = 'bold';
      } else {
        nextMatch = italicMatch;
        type = 'italic';
      }
    } else if (boldMatch) {
      nextMatch = boldMatch;
      type = 'bold';
    } else if (italicMatch) {
      nextMatch = italicMatch;
      type = 'italic';
    }

    if (!nextMatch) {
      parts.push(remaining);
      break;
    }

    if (nextMatch.index > 0) {
      parts.push(remaining.substring(0, nextMatch.index));
    }

    if (type === 'bold') {
      parts.push(
        <strong key={`b-${keyIdx}`} className="font-semibold text-gray-900">
          {nextMatch[1]}
        </strong>
      );
    } else {
      parts.push(
        <em key={`i-${keyIdx}`}>{nextMatch[1]}</em>
      );
    }

    keyIdx++;
    remaining = remaining.substring(nextMatch.index + nextMatch[0].length);
  }

  return parts;
};

const cleanText = (text) => text
  .replace(/\*\*/g, '')
  .replace(/\*/g, '')
  .replace(/_/g, '');

const renderParagraph = (paragraph, idx, options = {}) => {
  const { goldTitles = false } = options;

  if (paragraph.startsWith('##')) {
    const titleText = cleanText(paragraph.replace(/^#+\s*/, ''));
    const isSpecialTitle = goldTitles && (
      titleText.includes('Comment DÉVELOPPER') ||
      titleText.includes('Comment DÉMONTRER') ||
      titleText.includes('Comment ÉVALUER')
    );
    return (
      <h2
        key={`h2-${idx}`}
        className={`text-2xl font-bold mt-8 mb-4 ${isSpecialTitle ? 'text-gold' : 'text-gray-900'}`}
      >
        {titleText}
      </h2>
    );
  }

  if (paragraph.startsWith('####')) {
    return (
      <h4 key={`h4-${idx}`} className="text-lg font-bold text-gray-900 mt-5 mb-2">
        {cleanText(paragraph.replace(/^#+\s*/, ''))}
      </h4>
    );
  }

  if (paragraph.startsWith('###')) {
    return (
      <h3 key={`h3-${idx}`} className="text-xl font-bold text-gray-900 mt-6 mb-3">
        {cleanText(paragraph.replace(/^#+\s*/, ''))}
      </h3>
    );
  }

  if (paragraph.startsWith('#')) {
    return (
      <h3 key={`h3a-${idx}`} className="text-xl font-bold text-gray-900 mt-6 mb-3">
        {cleanText(paragraph.replace(/^#+\s*/, ''))}
      </h3>
    );
  }

  if (paragraph.startsWith('-')) {
    const items = paragraph.split('\n-').map(item => cleanText(item.replace(/^- /, '').trim()));
    return (
      <ul key={`ul-${idx}`} className="list-disc pl-6 space-y-2 my-4">
        {items.filter(Boolean).map((item, i) => (
          <li key={`li-${idx}-${i}`} className="text-gray-700">{item}</li>
        ))}
      </ul>
    );
  }

  if (paragraph.match(/^\d+\./)) {
    const items = paragraph.split(/\n\d+\./).map(item => cleanText(item.trim()));
    return (
      <ol key={`ol-${idx}`} className="list-decimal pl-6 space-y-2 my-4">
        {items.filter(Boolean).map((item, i) => (
          <li key={`oli-${idx}-${i}`} className="text-gray-700">{item}</li>
        ))}
      </ol>
    );
  }

  if (paragraph.startsWith('❌') || paragraph.startsWith('✔')) {
    return (
      <p key={`sp-${idx}`} className="text-gray-700 leading-relaxed my-3 pl-2">
        {paragraph}
      </p>
    );
  }

  // Safe rich text rendering (replaces dangerouslySetInnerHTML)
  if (paragraph.includes('**') || paragraph.includes('_')) {
    return (
      <p key={`rt-${idx}`} className="text-gray-700 leading-relaxed my-4">
        {parseRichText(paragraph)}
      </p>
    );
  }

  return (
    <p key={`p-${idx}`} className="text-gray-700 leading-relaxed my-4">
      {cleanText(paragraph)}
    </p>
  );
};

export const ArticleContent = ({ content, goldTitles = false }) => {
  const paragraphs = content.split('\n\n');

  return (
    <div className="prose prose-lg max-w-none">
      {paragraphs.map((paragraph, idx) =>
        renderParagraph(paragraph, idx, { goldTitles })
      )}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <p className="text-gray-900 font-semibold">Votre complice,</p>
        <p className="text-gold italic">Catherine Plante</p>
      </div>
    </div>
  );
};
